import React from 'react';

import { ThemeProvider } from '@emotion/react';
import { PAYPAL_THEME } from '@paypalcorp/pp-react-theme';
import Header from 'marketing-components/dist/components/organisms/header';
import Footer from 'marketing-components/dist/components/organisms/footer';

import * as ComponentsMap from '@paypalcorp/brc-components';

import PropTypes from 'prop-types';

import { NavFragment } from '@paypalcorp/globalnavsdk/NavFragment';

export const renderComponent = (
  { componentData, pageInfo, index },
  processComponentProps,
) => {
  let { componentType, ...componentProps } = componentData;
  const Component = ComponentsMap[componentType];
  const wasComponentFound = !!Component;

  if (!wasComponentFound) {
    return <div data-missed={componentType} />;
  }

  if (typeof processComponentProps === 'function') {
    componentProps = processComponentProps({
      componentProps,
      componentType,
      pageInfo,
    });
  }

  return <Component {...componentProps} key={`${componentType}-${index}`} />;
};

export const renderCmsComponents = (
  components,
  pageInfo,
  processComponentProps,
) =>
  components.map((componentData, index) =>
    renderComponent({ componentData, pageInfo, index }, processComponentProps),
  );

const { SubNav, Parent, ArticleStickyButton, Disclosure } = ComponentsMap;

const renderHeader = ({ header = {}, pageInfo = {} }) => {
  const { isLoggedIn = false, useGlobalNav = false } = pageInfo;
  if (useGlobalNav) {
    return <NavFragment type="header" />;
  }

  const { contentLoggedIn, contentLoggedOut, config } = header;
  const content = isLoggedIn ? contentLoggedIn : contentLoggedOut;
  const newConfig = {
    ...config,
    /* Setting always to 'v4' as we don't want the sticky behavior */
    version: 'v4',
  };

  // Once we start ramping Globalnav components we'll need to revisit this logic
  const isRebrandHeader = config?.dwHeader || config?.smbRebrand;
  const navElmtClassName = isRebrandHeader ? 'rebrand-nav' : 'legacy-nav';

  return (
    <Header content={content} config={newConfig} className={navElmtClassName} />
  );
};

const renderFooter = ({ footer = {}, pageInfo = {} }) => {
  const { useGlobalNav = false } = pageInfo;
  if (useGlobalNav) {
    return <NavFragment type="footer" />;
  }
  return <Footer {...footer} />;
};

const renderSubNav = ({ subNav }) => {
  if (!subNav) {
    return <div data-missed="SubNav" />;
  }

  // Checking if the SubNav has at least one Dropdown item
  const hasDropdownItems = subNav?.itemCollection?.some(
    (item) => item?.type === 'Dropdown',
  );

  return (
    <SubNav className={!hasDropdownItems && 'no-dropdown-items'} {...subNav} />
  );
};

const getParentProviderProps = ({
  fpti,
  pageInfo = {},
  isBrowserEnv = false,
}) => {
  return {
    pageSegment: pageInfo.pageSegment,
    fpti: isBrowserEnv ? fpti : {},
    isCMSPreview: pageInfo.isCMSPreview,
    runMode: pageInfo.runMode,
    csrfToken: pageInfo.csrf,
    pageURI: pageInfo.pageURI,
    clientInfo: pageInfo.clientInfo,
  };
};

const PageTemplate = ({
  articleStickyButton,
  children,
  className,
  footer,
  fpti = {},
  globalDisclosure,
  header,
  pageInfo,
  subNav,
}) => {
  const isBrowserEnv = typeof window !== 'undefined';

  const parentProviderProps = getParentProviderProps({
    fpti,
    pageInfo,
    isBrowserEnv,
  });

  return (
    <ThemeProvider theme={PAYPAL_THEME}>
      <Parent {...parentProviderProps}>
        {renderHeader({ header, pageInfo })}
        <div
          id="document-body-root"
          className={className}
          data-served-by={pageInfo.contentServedFrom}
          data-brc-experience="rebrand-experience"
        >
          {renderSubNav({ subNav })}
          {children}
          {articleStickyButton && (
            <ArticleStickyButton {...articleStickyButton} />
          )}
        </div>
        {renderFooter({ footer, pageInfo })}
        {globalDisclosure && (
          <section className="footer-disclosure grid">
            <div className="disclosure-container">
              <Disclosure {...globalDisclosure} />
            </div>
          </section>
        )}
      </Parent>
    </ThemeProvider>
  );
};

export const pageCommonPropTypes = {
  content: PropTypes.shape({
    globalComponents: PropTypes.shape({
      header: PropTypes.object.isRequired,
      footer: PropTypes.object.isRequired,
    }).isRequired,
    pageData: PropTypes.shape({
      articleStickyButton: PropTypes.shape({
        ariaLabel: PropTypes.string.isRequired,
      }),
      components: PropTypes.arrayOf(
        PropTypes.shape({
          componentType: PropTypes.string.isRequired,
        }),
      ).isRequired,
      subNav: PropTypes.object.isRequired,
      globalDisclosure: PropTypes.object.isRequired,
    }).isRequired,
  }).isRequired,
  pageInfo: PropTypes.object.isRequired,
  fpti: PropTypes.object,
};

PageTemplate.propTypes = {
  articleStickyButton: PropTypes.shape({
    ariaLabel: PropTypes.string.isRequired,
  }),
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  footer: PropTypes.object.isRequired,
  fpti: PropTypes.object,
  globalDisclosure: PropTypes.object,
  header: PropTypes.object.isRequired,
  pageInfo: PropTypes.object.isRequired,
  subNav: PropTypes.object.isRequired,
};

export default PageTemplate;
